import React, { Fragment, useState } from "react";
import Button from "../../../../../common/Button";
import { Menu, Transition } from "@headlessui/react";
import { ReactSVG } from "react-svg";
import { ChevronDownIcon } from "@heroicons/react/24/outline";

const Header = ({ text }) => (
  <p className="text-teal-300 text-[20px] leading-[20px] rounded-t-[12px] p-[20px] font-bold bg-teal-25">
    {text}
  </p>
);

const BackButton = ({ onClick, label }) => (
  <button onClick={onClick} className="flex items-center">
    <img src="/icons/arrow-left.svg" className="w-[20px]" alt="Back" />
    <span className="text-gray-800 ml-2 font-semibold">{label}</span>
  </button>
);

const Section = ({ header, children, onBack, backLabel }) => (
  <div className="flex flex-col rounded-t-[12px] h-full">
    <Header text={header} />
    <div className="flex flex-col p-[20px] h-full">
      <BackButton onClick={onBack} label={backLabel} />
      <div className="mt-6">{children}</div>
      <p className="self-start justify-end mt-auto text-[14px] text-gray-600">
        Reference: medSask
      </p>
    </div>
  </div>
);

const NonPharmacologicalCard = ({
  data,
  prescribedMedication = {},
  onAddClick = () => {},
  onEditClick = () => {},
  onDeleteClick = () => {},
}) => {
  const [activeItem, setActiveItem] = useState("");
  const [clickedItems, setClickedItems] = useState({
    clinicalPoints: false,
    specialPopulations: false,
  });
  const [viewedDrugs, setViewedDrugs] = useState([]);
  const handleButtonClick = (item) => {
    setActiveItem(item);
    //setClickedItems((prev) => ({ ...prev, [item]: true }));
  };

  const renderContent = () => {
    switch (activeItem) {
      case "clinicalPoints":
        return (
          <Section
            header={data.header}
            onBack={() => setActiveItem("")}
            backLabel="Clinical Points - Powered by medSask"
          >
            {data.clinicalPoints()}
          </Section>
        );
      case "specialPopulations":
        return (
          <Section
            header={data.header}
            onBack={() => setActiveItem("")}
            backLabel="Special Populations - Powered by medSask"
          >
            {data.specialPopulationPoints()}
          </Section>
        );
      default:
        return (
          <div className="flex flex-col justify-between rounded-t-[12px] h-full">
            <div className="flex flex-col">
              <Header text={data.header} />
              <div className="flex flex-col p-[20px] h-fit">
                <p className="text-gray-800 font-medium text-[20px] leading-[24px] min-h-[50px]">
                  {data.subheader}
                </p>
                <button
                  onClick={() => handleButtonClick("clinicalPoints")}
                  className="flex items-center justify-between p-[20px] mt-[20px] bg-gray-20 rounded-[10px]"
                  aria-label="Show Clinical Points"
                >
                  <p className="text-gray-800 text-[18px] leading-[20px] font-semibold">
                    Clinical Points
                  </p>
                  <img
                    src="/icons/arrow-right.svg"
                    className="max-h-[13px]"
                    alt="Arrow Right"
                  />
                </button>
                <button
                  onClick={() => handleButtonClick("specialPopulations")}
                  className="flex items-center justify-between p-[20px] mt-[10px] bg-gray-20 rounded-[10px]"
                  aria-label="Show Special Populations"
                >
                  <p className="text-gray-800 text-[18px] leading-[20px] font-semibold">
                    Special Populations
                  </p>
                  <img
                    src="/icons/arrow-right.svg"
                    className="max-h-[13px]"
                    alt="Arrow Right"
                  />
                </button>
              </div>
            </div>
            <div
              className={`flex items-center justify-between border-t-[1px] border-gray-100 p-[20px] ${
                prescribedMedication?.medicationName
                  ? (viewedDrugs?.includes(
                        prescribedMedication?.medicationName
                      ) ? "bg-blue-100 h-[140px]" : "bg-blue-100 h-[110px]")
                  : "h-[88px]"
              }`}
            >
              <div className="flex flex-col">
                <p className="text-gray-800 text-[14px] leading-[14px] font-medium">
                  Prescription(s):
                </p>
                <p
                  className={`text-gray-800 text-[16px] leading-[16px] mt-2 ${
                    prescribedMedication?.medicationName
                      ? "font-semibold"
                      : "font-medium"
                  }  capitalize`}
                >
                  {prescribedMedication?.medicationName ? (
                    <div className="flex flex-col gap-[2px]">
                      <div>{prescribedMedication?.medicationName}</div>
                      <div>
                        {prescribedMedication.route}{" "}
                        {prescribedMedication.dosage}{" "}
                        {prescribedMedication.form}{" "}
                        {prescribedMedication.frequency}{" "}
                        {prescribedMedication.prn && "PRN"}
                      </div>
                      <div className="grid grid-cols-2">
                        <p>QTY: {prescribedMedication.dispenseQuantity}</p>
                        <p>Refills: {prescribedMedication.refills? prescribedMedication.refills : 0}</p>
                      </div>
                      {viewedDrugs?.includes(
                        prescribedMedication?.medicationName
                      ) && (
                        <>
                        {prescribedMedication.duration &&
                          <div>
                            <p>Duration: {prescribedMedication.duration} {prescribedMedication.unit}</p>
                          </div>
                        }
                        {prescribedMedication.comments &&
                          <div>
                            <p>{prescribedMedication.comments}</p>
                          </div>
                        }
                        </>
                      )}
                    </div>
                  ) : (
                    "No prescription added."
                  )}
                </p>
              </div>
              {prescribedMedication?.medicationName ? (
                <div className="flex flex-col justify-center items-center gap-2">
                  <Menu as="div" className="relative inline-block text-left">
                    <div>
                      <Menu.Button>
                        <ReactSVG
                          src={`/icons/triple-dot.svg`}
                          className={`flex text-black !items-center w-2`}
                        />
                      </Menu.Button>
                    </div>
                    <Transition
                      as={Fragment}
                      enter="transition ease-out duration-100"
                      enterFrom="transform translate-y-2 opacity-0 scale-95"
                      enterTo="transform translate-y-0 opacity-100 scale-100"
                      leave="transition ease-in duration-75"
                      leaveFrom="transform translate-y-0 opacity-100 scale-100"
                      leaveTo="transform translate-y-2 opacity-0 scale-95"
                    >
                      <Menu.Items className="absolute right-0 bottom-0 mt-2 mr-10 w-24 origin-bottom-right bg-white divide-y divide-gray-20 rounded-md shadow-lg ring-1 ring-black ring-opacity-5">
                        <Menu.Item>
                          <button
                            className="block w-full text-left px-3 py-3 border-b border-b-gray-20 hover:bg-gray-100"
                            onClick={() => {
                              onEditClick(prescribedMedication);
                            }}
                          >
                            Edit
                          </button>
                        </Menu.Item>
                        <Menu.Item>
                          <button
                            className="block w-full text-left px-3 py-3 border-b border-b-gray-20 hover:bg-gray-100"
                            onClick={() => {
                              onDeleteClick(prescribedMedication);
                            }}
                          >
                            Delete
                          </button>
                        </Menu.Item>
                      </Menu.Items>
                    </Transition>
                  </Menu>
                {(prescribedMedication.duration || prescribedMedication.comments) &&  <ChevronDownIcon
                    className={`w-4 h-4 ${
                      viewedDrugs?.includes(
                        prescribedMedication?.medicationName
                      )
                        ? "-rotate-180"
                        : ""
                    }`}
                    onClick={() => {
                      setViewedDrugs(
                        !viewedDrugs?.includes(
                          prescribedMedication?.medicationName
                        )
                          ? [
                              ...viewedDrugs,
                              prescribedMedication?.medicationName,
                            ]
                          : viewedDrugs?.filter(
                              (x) => x !== prescribedMedication?.medicationName
                            )
                      );
                    }}
                  />}
                </div>
              ) : (
                <Button
                  text="Add"
                  iconUrl="/icons/add.svg"
                  onClick={onAddClick}
                  iconBefore
                  type="primary"
                  className="w-fit min-w-[120px] !rounded-[20px] h-[40px]"
                />
              )}
            </div>
          </div>
        );
    }
  };

  const isBlueBorder =
    clickedItems.clinicalPoints && clickedItems.specialPopulations;

  return (
    <div
      className={`flex flex-col rounded-[20px] w-full transition-colors duration-300 p-[20px] border-[6px] ${
        isBlueBorder ? "border-REELTeal border-solid" : " border-teal-75"
      } hover:border-REELTeal hover:border-solid`}
    >
      <div className="flex flex-col rounded-[12px] border-[1px] border-gray-20 shadow-custom h-[480px] overflow-auto">
        {renderContent()}
      </div>
    </div>
  );
};

export default NonPharmacologicalCard;
